import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import {
    useAcquirerLoadPhysicalToVirtualQuery,
    useAcquirerUpdatePhysicalToVirtualMutation,
} from "src/app/features/Acquirer/services/server/AcquirerApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { AcquirerEntity } from "../../types/AcquirerEntity";

export const AcquirerToVirtualEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id, physicalAcquirerId } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("acquirers-to-virtual");

    const [formData, setFormData] = useState<AcquirerEntity>({} as AcquirerEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useAcquirerLoadPhysicalToVirtualQuery({
        virtualAcquirerId: id!,
        entityId: physicalAcquirerId!,
    });

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAcquirerUpdatePhysicalToVirtualMutation();
    const completeSubmit = useFormSubmitCompletion(
        formAction,
        `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${id}`,
    );
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formUpdateSuccess", {
                        id: result.data.physicalAcquirerId,
                        resource: "Acquirer to Virtual",
                    }),
                );
            }
        },
        [saveFormData, formData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<AcquirerEntity>
                    name="AcquirerVirtualEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
